/**
 * studentHomeViewModel — l'accueil de l'élève (`<StudentHomePage>`), lu dans le store (patron A).
 *
 * Ce que montrait l'onglet legacy (`TodoListScreen.js`, branche élève, et son bandeau
 * `NextLiveSessionBanner.js`), sur les mêmes sources :
 *  - le compte (`currentUser.object`, `getUser`) : le salut et les compteurs (l'adaptateur de la
 *    coquille), le prochain live (`live_sessions`), le paiement en attente, le bandeau d'essai, la
 *    mission de Sam (`sam_mission`), la quête et le rang ;
 *  - `live_occurrences` (`getAllMyNextLiveSessions`) pour la carte du live ;
 *  - `schoolAssessments.byStudentId` (`getSchoolAssessments`, à venir) pour le prochain contrôle ;
 *  - `staticData` : les matières, les compétences des quêtes.
 * La liste des activités à faire n'est pas sur l'accueil (Wajih, 17/10) : l'onglet Activités l'a,
 * et la mission de Sam dit déjà ce qu'il y a à faire.
 *
 * La règle décidée vit ici, la page n'en garde pas (`homeSamMission`) : Sam n'est sur l'accueil
 * qu'un jour SANS live, avec un prochain live et Sam ouvert pour l'élève (`isSamOpenFor`). Il y porte
 * sa mission quand elle a au moins une activité ; sinon, comme l'ancien bandeau, « Étudie avec Sam en
 * attendant », sur la première activité à faire (Wajih, 19/10).
 *
 * La carte du live ne porte pas de phrase en plus (Wajih, 17/10) : « Camille t'attend à 18h00. »
 * redisait la date et l'heure du haut de la carte.
 *
 * Sous elle, les deux lives suivants en lignes courtes, puis « Tous mes lives » (Wajih, 18/10) : la
 * même lecture que « Mes prochains lives » de l'ancien onglet « Mon tutorat », aujourd'hui « Messages » (`live_occurrences`, par `toUILiveSessions`),
 * et sa même règle : une séance où l'absence est signalée sans rattrapage posé n'y est pas.
 *
 * L'anneau du prochain titre est celui de Mon compte (`toStudentTitleProgress`) : une seule formule.
 *
 * **Le premier affichage arrive d'un coup** (`studentHomeFirstDisplayLoading`) : l'accueil garde son
 * squelette tant que ses lectures principales ne sont pas là — le compte SERVI (la bascule de profil
 * ne pose d'abord que l'identité de l'élève : ni compteurs, ni lives, ni jeu) et les prochaines
 * séances — puis montre tout ensemble. Le prochain contrôle, lecture secondaire qui a sa propre
 * forme d'attente, ne le retient que `HOME_SECONDARY_HOLD_MS`.
 *
 * L'horloge est lue au calcul : le view-model se recalcule à chaque relecture du compte, que la
 * coquille relance quand le jour change (`useLiveAccessRefresh`). La carte du live, elle, garde
 * sa propre horloge pour son bouton.
 */
import moment from "moment"
import { createSelector } from "reselect"

import { isSamOpenFor } from "../../../../../data/constants/samConstants"
import { LIVE_DAY, liveDayFromToday } from "../../../../../data/utility/liveDay"
import { isBlockOn, TENANT_BLOCKS } from "../../../../../data/utility/tenantBlocks"
import { type ApiLiveOccurrence } from "../../../adapters/parent/LiveSessions/liveSessionListAdapter"
import {
    toUISchoolAssessments,
    type ApiSchoolAssessment,
} from "../../../adapters/parent/SchoolAssessments/schoolAssessmentListAdapter"
import { type ApiSchoolSubject } from "../../../adapters/parent/shared/schoolSubjectAdapter"
import {
    toStudentTitleProgress,
    type ApiSkill,
    type ApiStudentAccountUser,
} from "../../../adapters/student/Account/studentAccountAdapter"
import { toStudentShellAccount, type ApiStudentAccount } from "../../../adapters/student/shared/studentShellAdapter"
import {
    toStudentFollowingLives,
    toStudentNextLive,
    type ApiStudentLiveAccount,
} from "../../../adapters/student/Home/nextLiveAdapter"
import {
    toHomeQuest,
    toRankLabel,
    type ApiGameAccount,
    type ApiGameSkill,
} from "../../../adapters/student/shared/questAdapter"
import {
    toStudentSamMission,
    type ApiSamMission,
    type StudentSamMission,
} from "../../../adapters/student/Home/samMissionAdapter"
import { toStudentTrialTodo, type ApiTrialAccount } from "../../../adapters/student/Home/trialTodoAdapter"
import {
    type UILiveSession,
    type UISchoolAssessment,
    type UIStudentCounters,
    type UIStudentTitleProgress,
} from "../../../ui/types"
import { type UIQuest, type UITodoRowItem } from "../../../ui/types/studentHome"
import { selectDisplayedStudent } from "../../shared/displayedStudent"

type ApiHomeAccount = ApiStudentAccount &
    ApiStudentLiveAccount &
    ApiTrialAccount &
    ApiGameAccount &
    Pick<ApiStudentAccountUser, "roles" | "skill_sets" | "title_skill_set_id" | "student_title"> & {
        id?: string | number
        sam_eligible?: boolean
        sam_mission?: ApiSamMission | null
        /** Les activités à faire : la première est celle de « Étudie avec Sam en attendant ». */
        todo_activities?: { id?: string | number }[] | null
        is_payment_blocked?: boolean
        payment_url?: string | null
    }

type ApiSlice = { request?: boolean; error?: unknown }

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ReduxState = any

export type StudentHomeViewModel = {
    firstname: string
    counters: UIStudentCounters | null
    /** Le bandeau d'essai ; sa route est sur la ligne (`route`). */
    trialTodo: UITodoRowItem | null
    nextSession: UILiveSession | null
    /** Les deux lives qui suivent le prochain, en lignes courtes sous sa carte. */
    followingLives: UILiveSession[]
    /** L'anneau du prochain titre, celui de Mon compte. */
    titleProgress: UIStudentTitleProgress | null
    /** La séance que « GO ! » ouvre dans `pre-call`. */
    nextLiveSessionId: string | number | null
    /**
     * Sam sur l'accueil, quand la règle le donne : sa mission, ou « Étudie avec Sam en attendant »
     * (`kind: "study"`). L'activité qu'il ouvre y est (`nextActivityId`).
     */
    samMission: StudentSamMission | null
    assessment: UISchoolAssessment | null
    /** La première lecture des contrôles de cet élève est en route. */
    assessmentLoading: boolean
    quest: UIQuest | null
    rankLabel: string | null
    /**
     * Paiement en attente : « GO ! » ouvre la boîte de régularisation, jamais le live ; le bouton de la carte
     * de Sam aussi, jamais Sam (l'accueil ne l'invite qu'à régulariser ; Activités reste ouvert).
     */
    paymentBlocked: boolean
    paymentUrl: string | null
    studentId: string | number | null
    /**
     * Ce que le store a déjà, pour le premier affichage (`studentHomeFirstDisplayLoading`) : le compte
     * servi, et des séances ou des contrôles gardés d'une ouverture précédente.
     */
    firstDisplay: { accountServed: boolean; livesKept: boolean; assessmentsKept: boolean }
    loading: boolean
    error: unknown
}

/**
 * Le compte est-il celui que sert `/api/user`, ou seulement l'identité que pose la bascule de profil
 * (`SWITCH_ACCOUNT` : nom, rôles, avatar) en attendant ? Le compte servi d'un élève porte toujours
 * son XP ; l'identité, jamais.
 */
export function isServedStudentAccount(user: { xp?: unknown } | null | undefined): boolean {
    return user != null && user.xp !== undefined
}

/** Le prochain contrôle ne retient pas l'accueil plus de 500 ms : il a sa propre forme d'attente. */
export const HOME_SECONDARY_HOLD_MS = 500

/**
 * L'accueil montre-t-il encore son squelette ? Un seul squelette, puis tout d'un coup : tant que le
 * compte n'est pas servi, ou que les prochaines séances ne sont pas arrivées (ni lues pour ce premier
 * affichage, ni gardées d'une ouverture précédente). Le prochain contrôle, lecture secondaire, ne
 * retient l'accueil que le temps de `HOME_SECONDARY_HOLD_MS` ; au-delà, sa carte attend seule.
 * Une lecture qui échoue compte comme arrivée : l'accueil montre ce qu'il a, jamais un squelette sans fin.
 */
export function studentHomeFirstDisplayLoading(args: {
    /** Pas de compte du tout (`loading` du view-model). */
    accountMissing: boolean
    accountServed: boolean
    /** La lecture du compte a échoué : on montre l'identité qu'on a. */
    accountFailed: boolean
    livesArrived: boolean
    assessmentsArrived: boolean
    secondaryHoldElapsed: boolean
}): boolean {
    const { accountMissing, accountServed, accountFailed, livesArrived, assessmentsArrived, secondaryHoldElapsed } =
        args
    if (accountMissing) return true
    if (!accountServed && !accountFailed) return true
    if (!livesArrived) return true
    return !assessmentsArrived && !secondaryHoldElapsed
}

/**
 * Sam sur l'accueil — la règle de Wajih : un jour sans live, avec un prochain live, et Sam ouvert
 * pour l'élève. Là, sa mission quand elle a au moins une activité (accomplie, elle reste jusqu'au
 * live). Sans mission, l'ancien bandeau (`NextLiveSessionBanner.js`, `samBannerActive`) : « Étudie
 * avec Sam en attendant », qui ouvre avec Sam la première activité à faire (`todo_activities[0]`) —
 * et rien quand il n'y en a aucune, comme lui.
 */
export function homeSamMission(args: {
    samOpen: boolean
    nextLiveDate: string | null
    mission: StudentSamMission | null
    /** La première activité à faire (`todo_activities[0]`), celle qu'ouvrait l'ancien bandeau. */
    firstTodoActivityId?: string | number | null
    now?: moment.Moment
}): StudentSamMission | null {
    const { samOpen, nextLiveDate, mission, firstTodoActivityId = null, now = moment() } = args
    if (!samOpen || nextLiveDate == null) return null
    if (liveDayFromToday(nextLiveDate, now) === LIVE_DAY.TODAY) return null
    if (mission != null && mission.total > 0) return mission
    if (firstTodoActivityId == null) return null
    return { kind: "study", done: 0, total: 0, nextActivityId: firstTodoActivityId }
}

export const studentHomeViewModel = createSelector(
    [
        (state: ReduxState) => selectDisplayedStudent(state) as ApiHomeAccount | undefined,
        (state: ReduxState) => state?.data?.liveSessions?.live_occurrences as ApiLiveOccurrence[] | undefined,
        (state: ReduxState) => state?.data?.staticData?.school_subjects as ApiSchoolSubject[] | undefined,
        (state: ReduxState) => state?.data?.staticData?.skills as (ApiGameSkill & ApiSkill)[] | undefined,
        (state: ReduxState) =>
            state?.data?.schoolAssessments?.byStudentId as Record<string, ApiSchoolAssessment[]> | undefined,
        // L'état de `getUser` ne sert que tant que le compte n'est pas là : une fois chargé, ses
        // relectures (trois transitions chacune) ne recalculent pas l'accueil.
        (state: ReduxState) =>
            selectDisplayedStudent(state) == null && !!(state?.api?.getUser as ApiSlice | undefined)?.request,
        (state: ReduxState) =>
            selectDisplayedStudent(state) == null ? (state?.api?.getUser as ApiSlice | undefined)?.error : null,
        (state: ReduxState) => !!(state?.api?.getSchoolAssessments as ApiSlice | undefined)?.request,
        (state: ReduxState) => (state?.api?.getSchoolAssessments as ApiSlice | undefined)?.error != null,
    ],
    (
        user,
        occurrences,
        subjects,
        skills,
        assessmentsByStudentId,
        userRequesting,
        userError,
        assessmentsRequesting,
        assessmentsFailed
    ): StudentHomeViewModel => {
        const now = moment()
        const shell = toStudentShellAccount(user)
        const studentId = user?.id ?? null

        const nextLive = toStudentNextLive(user, occurrences, subjects, now)
        const nextSession = nextLive?.session ?? null
        const samMission = homeSamMission({
            samOpen: isSamOpenFor(user),
            nextLiveDate: nextSession?.date ?? null,
            mission: toStudentSamMission(user?.sam_mission),
            firstTodoActivityId: user?.todo_activities?.[0]?.id ?? null,
            now,
        })

        const assessments = studentId != null ? assessmentsByStudentId?.[String(studentId)] : undefined
        const assessment = toUISchoolAssessments(assessments)[0]
        const assessmentsKept = assessments !== undefined

        // « Pétales, défis, XP » éteint dans son espace : ni quête, ni classement, ni titre.
        const game = isBlockOn(user, TENANT_BLOCKS.GAMIFICATION)

        return {
            firstname: shell.firstname,
            counters: shell.counters,
            trialTodo: toStudentTrialTodo(user, now),
            nextSession,
            followingLives: toStudentFollowingLives(user, occurrences, subjects, nextLive),
            titleProgress: game ? toStudentTitleProgress(user as ApiStudentAccountUser | undefined, skills) : null,
            nextLiveSessionId: nextLive?.liveSessionId ?? null,
            samMission,
            assessment: assessment ?? null,
            // Pas encore de réponse pour cet élève, et pas d'échec : sa carte attend, même avant que la
            // lecture ne parte (au premier rendu, le widget ne doit pas dire « Ajouter un contrôle »).
            assessmentLoading: !assessmentsKept && (assessmentsRequesting || !assessmentsFailed),
            quest: game ? toHomeQuest(user, skills) : null,
            rankLabel: game ? toRankLabel(user) : null,
            paymentBlocked: user?.is_payment_blocked === true,
            paymentUrl: user?.payment_url ?? null,
            studentId,
            firstDisplay: {
                accountServed: isServedStudentAccount(user),
                livesKept: (occurrences?.length ?? 0) > 0,
                assessmentsKept,
            },
            loading: user == null && userRequesting,
            error: user == null ? (userError ?? null) : null,
        }
    }
)
